import { useState } from 'react'
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, Menu, MenuItem, Select,
  Stack, Tooltip, Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import LinkIcon from '@mui/icons-material/Link'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '@cmms/api-client'
import type { User, Role, Team, Site } from '@cmms/types'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import { useConfirm } from '../../../components/ConfirmDialog'
import { useAuthStore } from '../../../store/authStore'
import { usePermissions } from '../../../hooks/usePermissions'
import { useCopyLink } from '../../../hooks/useCopyLink'
import { useOrgSettings } from '../../../hooks/useOrgSettings'
import { SearchableSelect } from '../../../components/SearchableSelect'

const ROLE_COLORS: Record<string, string> = {
  org_admin: '#0D9488', admin: '#0D9488', manager: '#059669', technician: '#D97706', viewer: '#64748B',
}

interface Props {
  user: User
  roles: Role[]
  teams: Team[]
}

export function MemberDetail({ user, roles, teams }: Props) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const confirmDlg = useConfirm()
  const copyLink = useCopyLink()
  const me = useAuthStore((s) => s.user)
  const { can } = usePermissions()
  const { formatDate, formatDateShort } = useOrgSettings()
  const isSelf = me?.id === user.id

  const canUpdate = can('users:update') && !isSelf
  const canDelete = can('users:delete') && !isSelf

  const [roleDialogOpen, setRoleDialogOpen] = useState(false)
  const [newRole, setNewRole] = useState(user.role)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  const sitesQ = useQuery({
    queryKey: ['user-sites', user.id],
    queryFn: () => api.users.listSites(user.id),
  })

  function asMsg(err: unknown, fallback: string) {
    return err instanceof ApiError ? err.message : fallback
  }

  const deactivateM = useMutation({
    mutationFn: () => api.users.deactivate(user.id),
    onSuccess: () => { toast.success('User deactivated'); qc.invalidateQueries({ queryKey: ['users'] }) },
    onError: (e) => toast.error(asMsg(e, 'Failed to deactivate')),
  })
  const reactivateM = useMutation({
    mutationFn: () => api.users.reactivate(user.id),
    onSuccess: () => { toast.success('User reactivated'); qc.invalidateQueries({ queryKey: ['users'] }) },
    onError: (e) => toast.error(asMsg(e, 'Failed to reactivate')),
  })
  const changeRoleM = useMutation({
    mutationFn: (roleId: string) => api.users.updateRole(user.id, roleId),
    onSuccess: () => {
      toast.success('Role updated')
      qc.invalidateQueries({ queryKey: ['users'] })
      setRoleDialogOpen(false)
    },
    onError: (e) => toast.error(asMsg(e, 'Failed to change role')),
  })
  const resetPwM = useMutation({
    mutationFn: () => api.users.adminResetPassword(user.id),
    onSuccess: () => toast.success('Password reset email sent'),
    onError: (e) => toast.error(asMsg(e, 'Failed to send reset')),
  })
  const forceLogoutM = useMutation({
    mutationFn: () => api.users.forceLogout(user.id),
    onSuccess: () => toast.success('All sessions revoked — they will be logged out immediately'),
    onError: (e) => toast.error(asMsg(e, 'Failed to force logout')),
  })
  const deleteM = useMutation({
    mutationFn: () => api.users.remove(user.id),
    onSuccess: () => { toast.success('User deleted'); qc.invalidateQueries({ queryKey: ['users'] }) },
    onError: (e) => toast.error(asMsg(e, 'Failed to delete')),
  })

  const roleColor = ROLE_COLORS[user.role] ?? '#64748B'
  const initials = user.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()

  const noPermTip = "You don't have permission"

  return (
    <motion.div
      key={user.id}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26, ease: [0.32, 0.72, 0, 1] }}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}
    >
      {/* Header */}
      <Box sx={{ px: 4, pt: 4, pb: 3, flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2.5}>
          <Avatar
            src={user.avatar_url ?? undefined}
            sx={{ width: 56, height: 56, bgcolor: roleColor, fontSize: 20, fontWeight: 700, flexShrink: 0 }}
          >
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              {user.name}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
              <Chip
                label={formatRole(user.role)}
                size="small"
                sx={{ height: 22, fontSize: 11.5, fontWeight: 700, bgcolor: `${roleColor}18`, color: roleColor, '& .MuiChip-label': { px: 1 } }}
              />
              <Chip
                label={user.is_active ? 'Active' : 'Deactivated'}
                size="small"
                sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: 'action.hover', color: user.is_active ? '#059669' : '#94A3B8', '& .MuiChip-label': { px: 1 } }}
              />
              {isSelf && (
                <Chip label="You" size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: 'primary.main', color: 'primary.contrastText', '& .MuiChip-label': { px: 0.85 } }} />
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
            <Tooltip title="Copy link" arrow>
              <IconButton size="small" sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
                <LinkIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={isSelf ? 'View your profile in Settings' : 'Actions'} arrow>
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}
              >
                <MoreHorizIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* ─── More actions menu (always visible, items disabled by permission) ─── */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            slotProps={{ paper: { sx: { minWidth: 240, borderRadius: 1.5 } } }}
          >
            {isSelf && (
              <MenuItem disabled sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                <LockOutlinedIcon sx={{ fontSize: 14, mr: 1.5 }} />
                You can't manage your own account here
              </MenuItem>
            )}
            {!isSelf && (
              <Box>
                <Tooltip title={canUpdate ? '' : noPermTip} placement="left" arrow>
                  <span>
                    <MenuItem
                      disabled={!canUpdate}
                      onClick={() => { setMenuAnchor(null); setNewRole(user.role); setRoleDialogOpen(true) }}
                      sx={{ fontSize: 13 }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} />
                      Change role
                    </MenuItem>
                  </span>
                </Tooltip>
                <Tooltip title={canUpdate ? '' : noPermTip} placement="left" arrow>
                  <span>
                    {user.is_active ? (
                      <MenuItem
                        disabled={!canUpdate}
                        onClick={() => { setMenuAnchor(null); confirmDlg({ title: `Deactivate ${user.name}?`, message: "This will revoke access and log out all sessions.", confirmLabel: "Deactivate", variant: "danger" }).then((ok) => ok && deactivateM.mutate()) }}
                        sx={{ fontSize: 13 }}
                      >
                        <BlockOutlinedIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} />
                        Deactivate
                      </MenuItem>
                    ) : (
                      <MenuItem
                        disabled={!canUpdate}
                        onClick={() => { setMenuAnchor(null); reactivateM.mutate() }}
                        sx={{ fontSize: 13 }}
                      >
                        <CheckCircleOutlineIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} />
                        Reactivate
                      </MenuItem>
                    )}
                  </span>
                </Tooltip>
                <Tooltip title={canUpdate ? '' : noPermTip} placement="left" arrow>
                  <span>
                    <MenuItem
                      disabled={!canUpdate}
                      onClick={() => { setMenuAnchor(null); resetPwM.mutate() }}
                      sx={{ fontSize: 13 }}
                    >
                      <KeyOutlinedIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} />
                      Reset password
                    </MenuItem>
                  </span>
                </Tooltip>
                <Tooltip title={canUpdate ? '' : noPermTip} placement="left" arrow>
                  <span>
                    <MenuItem
                      disabled={!canUpdate}
                      onClick={() => { setMenuAnchor(null); forceLogoutM.mutate() }}
                      sx={{ fontSize: 13 }}
                    >
                      <LogoutOutlinedIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} />
                      Force logout
                    </MenuItem>
                  </span>
                </Tooltip>
                <Divider />
                <Tooltip title={canDelete ? '' : noPermTip} placement="left" arrow>
                  <span>
                    <MenuItem
                      disabled={!canDelete}
                      onClick={() => { setMenuAnchor(null); confirmDlg({ title: `Delete ${user.name}?`, message: "This will permanently remove the user. This cannot be undone.", confirmLabel: "Delete", variant: "danger" }).then((ok) => ok && deleteM.mutate()) }}
                      sx={{ fontSize: 13, color: canDelete ? 'error.main' : 'text.disabled' }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16, mr: 1.5 }} />
                      Delete user
                    </MenuItem>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Menu>
        </Stack>
      </Box>

      <Divider />

      {/* Scrollable body */}
      <Box
        sx={{
          flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3,
          scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
          '&:hover::-webkit-scrollbar-thumb': {
            background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)',
          },
        }}
      >
        {/* Contact */}
        <Section title="Contact">
          <Stack spacing={1.25}>
            <InfoRow icon={<EmailOutlinedIcon sx={{ fontSize: 15 }} />} label="Email" value={user.email} />
            {user.phone && <InfoRow icon={<PhoneOutlinedIcon sx={{ fontSize: 15 }} />} label="Phone" value={user.phone} />}
            <InfoRow
              icon={<AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />}
              label="Last active"
              value={relativeTime(user.last_action_at ?? user.last_login_at, formatDateShort)}
            />
            <InfoRow icon={<PersonOutlineIcon sx={{ fontSize: 15 }} />} label="Logins" value={String(user.login_count)} />
          </Stack>
        </Section>

        <Divider />

        {/* Customer access — drives X-Customer-Id visibility. Empty
            = user sees nothing; full set = same as org_admin scoping. */}
        <CustomerAccessSection user={user} canManage={canUpdate} />

        <Divider />

        {/* Site access */}
        <SiteAccessSection user={user} canManage={canUpdate} />

        <Divider />

        {/* Quick actions — always visible, disabled when no permission */}
        {!isSelf && (
          <>
            <Section title="Quick actions">
              <Stack spacing={1}>
                <ActionRow
                  icon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
                  label="Change role"
                  description={`Current: ${formatRole(user.role)}`}
                  onClick={() => { setNewRole(user.role); setRoleDialogOpen(true) }}
                  disabled={!canUpdate}
                />
                {user.is_active ? (
                  <ActionRow
                    icon={<BlockOutlinedIcon sx={{ fontSize: 15 }} />}
                    label="Deactivate"
                    description="Revoke access and log out all sessions"
                    onClick={() => { confirmDlg({ title: `Deactivate ${user.name}?`, message: "This will revoke access and log out all sessions.", confirmLabel: "Deactivate", variant: "danger" }).then((ok) => ok && deactivateM.mutate()) }}
                    danger disabled={!canUpdate}
                  />
                ) : (
                  <ActionRow
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
                    label="Reactivate"
                    description="Restore access to the workspace"
                    onClick={() => reactivateM.mutate()}
                    disabled={!canUpdate}
                  />
                )}
                <ActionRow
                  icon={<KeyOutlinedIcon sx={{ fontSize: 15 }} />}
                  label="Reset password"
                  description="Send a password reset email"
                  onClick={() => resetPwM.mutate()}
                  disabled={!canUpdate}
                />
                <ActionRow
                  icon={<LogoutOutlinedIcon sx={{ fontSize: 15 }} />}
                  label="Force logout"
                  description="Revoke all active sessions"
                  onClick={() => forceLogoutM.mutate()}
                  disabled={!canUpdate}
                />
                <ActionRow
                  icon={<DeleteOutlineIcon sx={{ fontSize: 15 }} />}
                  label="Delete user"
                  description="Permanently remove from the workspace"
                  onClick={() => { confirmDlg({ title: `Delete ${user.name}?`, message: "This will permanently remove the user. This cannot be undone.", confirmLabel: "Delete", variant: "danger" }).then((ok) => ok && deleteM.mutate()) }}
                  danger disabled={!canDelete}
                />
              </Stack>
            </Section>
            <Divider />
          </>
        )}

        {/* Metadata */}
        <Section title="Account">
          <InfoRow
            icon={<AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />}
            label="Added"
            value={formatDate(user.created_at)}
          />
        </Section>
      </Box>

      {/* HSE fork: the CMMS bottom-CTA row hosted "Assign task" and
          "Assign work order" shortcuts. Both modules were stripped
          from HSE, so the whole row is gone — the member detail
          ends after the info stack above. */}

      {/* Change role dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Change role</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
            Pick a new role for {user.name}.
          </Typography>
          <SearchableSelect
            value={newRole}
            onChange={setNewRole}
            options={roles}
            getValue={(r) => r.name}
            getLabel={(r) => `${r.name}${r.is_system ? ' (system)' : ''}`}
            placeholder="Select a role"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRoleDialogOpen(false)} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => changeRoleM.mutate(newRole)}
            disabled={changeRoleM.isPending}
            sx={{ fontSize: 13, fontWeight: 600 }}
          >
            {changeRoleM.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  )
}

// ─── Building blocks ────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ py: 2.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em', mb: 1.25 }}>{title}</Typography>
      {children}
    </Box>
  )
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{ color: 'text.secondary', display: 'inline-flex', flexShrink: 0 }}>{icon}</Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', minWidth: 80 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary' }}>{value}</Typography>
    </Stack>
  )
}

function ActionRow({
  icon, label, description, onClick, danger = false, disabled = false,
}: {
  icon: React.ReactNode; label: string; description: string
  onClick: () => void; danger?: boolean; disabled?: boolean
}) {
  return (
    <Tooltip title={disabled ? "You don't have permission" : ''} placement="left" arrow>
      <Stack
        direction="row" alignItems="center" spacing={1.5}
        onClick={disabled ? undefined : onClick}
        sx={{
          px: 1.5, py: 1.25, borderRadius: 1.25,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.45 : 1,
          transition: 'background-color 120ms ease, opacity 120ms ease',
          '&:hover': disabled ? {} : {
            bgcolor: danger
              ? (t) => t.palette.mode === 'light' ? 'rgba(220,38,38,0.06)' : 'rgba(220,38,38,0.12)'
              : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
          },
        }}
      >
        <Box sx={{ color: danger ? '#DC2626' : 'text.secondary', display: 'inline-flex', flexShrink: 0 }}>{icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em', color: danger ? '#DC2626' : 'text.primary' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{description}</Typography>
        </Box>
        {disabled && <LockOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0, opacity: 0.7 }} />}
      </Stack>
    </Tooltip>
  )
}

function SiteAccessSection({ user, canManage }: { user: User; canManage: boolean }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([])

  const sitesQ = useQuery({
    queryKey: ['user-sites', user.id],
    queryFn: () => api.users.listSites(user.id),
  })
  const userSites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data as any : []

  const allSitesQ = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.sites.list(),
    staleTime: 5 * 60 * 1000,
  })
  const allSites: Site[] = Array.isArray(allSitesQ.data) ? allSitesQ.data : []
  const unassigned = allSites.filter((s) => !userSites.some((us) => us.id === s.id))

  const grantM = useMutation({
    mutationFn: (siteIds: string[]) => api.users.grantSites(user.id, siteIds),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-sites', user.id] }); toast.success('Site access granted'); setAdding(false); setSelectedSiteIds([]) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const revokeM = useMutation({
    mutationFn: (siteId: string) => api.users.revokeSite(user.id, siteId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-sites', user.id] }); toast.success('Site access revoked') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const isAdmin = user.role === 'org_admin'
  // customer_admin gets the whole customer's site set automatically
  // via customer scope — explicit per-site grants would be ignored
  // server-side (see `getAccessibleSiteIds`). Surface that here so an
  // admin doesn't waste time toggling sites that wouldn't take effect.
  const isCustomerAdmin = user.role === 'customer_admin'

  return (
    <Section title="Site access">
      {sitesQ.isLoading ? (
        <CircularProgress size={18} />
      ) : isAdmin ? (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1, py: 0.75, borderRadius: 1, bgcolor: (t) => `${t.palette.primary.main}08` }}>
          <LocationOnOutlinedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
          <Typography sx={{ fontSize: 12.5, color: 'primary.main', fontWeight: 500 }}>Full access to all sites (admin)</Typography>
        </Stack>
      ) : isCustomerAdmin ? (
        <Stack direction="row" spacing={1}
          sx={{
            px: 1.25, py: 1, borderRadius: 1.5,
            bgcolor: (t) => `${t.palette.info.main}0F`,
            border: '1px solid',
            borderColor: (t) => `${t.palette.info.main}33`,
          }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main', mt: '1px' }} />
          <Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'info.main' }}>
              Customer admins see every site in their assigned customer(s)
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25, lineHeight: 1.5 }}>
              Per-site grants don't apply to this role — visibility is driven by the user's customer assignments under the
              {' '}<Typography component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.primary' }}>Customer access</Typography>{' '}
              section. To restrict a person to a subset of a customer's sites, use the
              {' '}<Typography component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.primary' }}>Technician</Typography>{' '}
              or
              {' '}<Typography component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.primary' }}>Viewer</Typography>{' '}
              role instead.
            </Typography>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {userSites.length > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {userSites.map((s) => (
                <Chip key={s.id} label={s.name} size="small"
                  onDelete={canManage ? () => revokeM.mutate(s.id) : undefined}
                  deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                  sx={{ height: 28, fontSize: 12, fontWeight: 500, '& .MuiChip-deleteIcon': { color: 'text.secondary', '&:hover': { color: 'error.main' } } }} />
              ))}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No sites assigned — this user can't see any site-specific data.</Typography>
          )}

          {canManage && !adding && (
            unassigned.length > 0 ? (
              <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setAdding(true)}
                sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main', alignSelf: 'flex-start' }}>
                Add site access
              </Button>
            ) : allSites.length > 0 ? (
              <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>All sites are already assigned.</Typography>
            ) : (
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                No sites created yet. <Typography component="a" href="/settings/sites" sx={{ fontSize: 11.5, color: 'primary.main', cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Create a site first</Typography>
              </Typography>
            )
          )}

          {adding && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
                Select sites to grant access
              </Typography>
              <Stack spacing={0.5} sx={{ maxHeight: 160, overflowY: 'auto', scrollbarWidth: 'thin', mb: 1.5 }}>
                {unassigned.map((s) => (
                  <Stack key={s.id} direction="row" alignItems="center" spacing={1}
                    onClick={() => setSelectedSiteIds((prev) => prev.includes(s.id) ? prev.filter((id) => id !== s.id) : [...prev, s.id])}
                    sx={{
                      px: 1.25, py: 0.75, borderRadius: 1, cursor: 'pointer',
                      bgcolor: selectedSiteIds.includes(s.id) ? (t) => `${t.palette.primary.main}12` : 'transparent',
                      border: '1px solid', borderColor: selectedSiteIds.includes(s.id) ? 'primary.main' : 'divider',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: 'primary.main' },
                    }}>
                    <Box sx={{
                      width: 18, height: 18, borderRadius: 0.5, border: '2px solid',
                      borderColor: selectedSiteIds.includes(s.id) ? 'primary.main' : 'divider',
                      bgcolor: selectedSiteIds.includes(s.id) ? 'primary.main' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {selectedSiteIds.includes(s.id) && <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#fff' }} />}
                    </Box>
                    <Typography sx={{ fontSize: 12.5 }}>{s.name}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="contained" disabled={selectedSiteIds.length === 0 || grantM.isPending}
                  onClick={() => grantM.mutate(selectedSiteIds)}
                  sx={{ fontSize: 12 }}>
                  {grantM.isPending ? 'Granting...' : `Grant access (${selectedSiteIds.length})`}
                </Button>
                <Button size="small" onClick={() => { setAdding(false); setSelectedSiteIds([]) }}
                  sx={{ fontSize: 12, color: 'text.secondary' }}>Cancel</Button>
              </Stack>
            </Box>
          )}
        </Stack>
      )}
    </Section>
  )
}

/// Customer-access editor — mirrors SiteAccessSection one level up.
/// Empty set = user sees no customer data; full set = same scoping as
/// org_admin's "All customers" mode. org_admin bypasses this entirely
/// (they always see everything); the section shows the bypass banner
/// in that case.
function CustomerAccessSection({ user, canManage }: { user: User; canManage: boolean }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)

  const userCustomersQ = useQuery({
    queryKey: ['user-customers', user.id],
    queryFn: () => api.customers.listForUser(user.id),
  })
  const userCustomers = userCustomersQ.data ?? []

  const allCustomersQ = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list(),
    staleTime: 5 * 60 * 1000,
  })
  const allCustomers = allCustomersQ.data ?? []
  const unassigned = allCustomers.filter((c) => !userCustomers.some((uc) => uc.id === c.id))

  const setM = useMutation({
    mutationFn: (ids: string[]) => api.customers.setForUser(user.id, ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-customers', user.id] })
      // Switcher's accessible list may also change — refresh that too.
      qc.invalidateQueries({ queryKey: ['customers'] })
      toast.success('Customer access updated')
      setAdding(false)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const isAdmin = user.role === 'org_admin'

  // Single-customer orgs: hide the section to keep noise down.
  if (allCustomersQ.isSuccess && allCustomers.length <= 1) return null

  return (
    <Section title="Customer access">
      {userCustomersQ.isLoading ? (
        <CircularProgress size={18} />
      ) : isAdmin ? (
        <Stack direction="row" alignItems="center" spacing={0.75}
          sx={{ px: 1, py: 0.75, borderRadius: 1, bgcolor: (t) => `${t.palette.primary.main}08` }}>
          <BusinessOutlinedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
          <Typography sx={{ fontSize: 12.5, color: 'primary.main', fontWeight: 500 }}>
            Full access to all customers (admin)
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {userCustomers.length > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {userCustomers.map((c) => (
                <Chip
                  key={c.id}
                  icon={<BusinessOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={c.name}
                  size="small"
                  onDelete={canManage
                    ? () => setM.mutate(userCustomers.filter((x) => x.id !== c.id).map((x) => x.id))
                    : undefined}
                  deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                  sx={{ height: 28, fontSize: 12, fontWeight: 500,
                    '& .MuiChip-deleteIcon': { color: 'text.secondary', '&:hover': { color: 'error.main' } } }}
                />
              ))}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              No customers assigned — this user can't see any customer data.
            </Typography>
          )}

          {canManage && !adding && unassigned.length > 0 && (
            <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setAdding(true)}
              sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main', alignSelf: 'flex-start' }}>
              Add customer access
            </Button>
          )}

          {adding && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
                Grant access to
              </Typography>
              <Stack spacing={0.5}>
                {unassigned.map((c) => (
                  <Box key={c.id} onClick={() => setM.mutate([...userCustomers.map((x) => x.id), c.id])}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.25, py: 0.75,
                      cursor: setM.isPending ? 'wait' : 'pointer', borderRadius: 1,
                      opacity: setM.isPending ? 0.6 : 1,
                      '&:hover': { bgcolor: 'action.hover' } }}>
                    <BusinessOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{c.name}</Typography>
                      {typeof c.site_count === 'number' && (
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                          {c.site_count} site{c.site_count === 1 ? '' : 's'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
              <Button size="small" onClick={() => setAdding(false)}
                sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>Done</Button>
            </Box>
          )}
        </Stack>
      )}
    </Section>
  )
}

function formatRole(role: string): string {
  return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function relativeTime(iso: string | null, formatDateShort: (d: Date | string) => string): string {
  if (!iso) return 'Never'
  const diff = Date.now() - new Date(iso).getTime()
  if (diff < 60_000) return 'Just now'
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return formatDateShort(iso)
}
